import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { vendureCacheConfig } from "@/api/vendure/cache.config";
import type { AttachCustomerPayload } from "@/api/vendure/order/order.types";
import { apiClient } from "@/lib/api-client";
import { vendure } from "@/lib/vendure";
import type {
  AuthCustomer,
  AuthResponse,
  AuthResponseV2,
  CompleteProfileInput,
  CreateAddressInput,
  CustomerAddress,
} from "./auth.types";

export const requestCodeFn = createServerFn({ method: "POST" })
  .inputValidator((d: { countryCode: string; phoneNumber: string }) => d)
  .handler(async ({ data }) => {
    await vendure.post("/api/v1/phone-auth/request-code", {
      countryCode: data.countryCode,
      phoneNumber: data.phoneNumber,
      type: "sms",
    });
  });

export const verifyCodeFn = createServerFn({ method: "POST" })
  .inputValidator(
    (d: { countryCode: string; phoneNumber: string; code: string }) => d,
  )
  .handler(async ({ data }): Promise<AuthResponse> => {
    const { data: res } = await vendure.post<AuthResponse>(
      "/api/v1/phone-auth/verify-code",
      {
        countryCode: data.countryCode,
        phoneNumber: data.phoneNumber,
        code: data.code,
      },
    );
    return res;
  });

export const verifyCodeV2Fn = createServerFn({ method: "POST" })
  .inputValidator(
    (d: { countryCode: string; phoneNumber: string; code: string }) => d,
  )
  .handler(async ({ data }): Promise<AuthResponseV2> => {
    const { data: res } = await vendure.post<AuthResponseV2>(
      "/api/v2/phone-auth/verify-code",
      {
        countryCode: data.countryCode,
        phoneNumber: data.phoneNumber,
        code: data.code,
      },
    );
    return res;
  });

export const completeProfileFn = createServerFn({ method: "POST" })
  .inputValidator((d: CompleteProfileInput) => d)
  .handler(async ({ data }): Promise<AuthResponse> => {
    const { data: res } = await vendure.post<AuthResponse>(
      "/api/v2/phone-auth/complete-profile",
      data,
    );
    return res;
  });

export const mintGuestTokenFn = createServerFn({ method: "POST" }).handler(
  async (): Promise<{ token: string; expiresAt: string }> => {
    const { data } = await vendure.post<{ token: string; expiresAt: string }>(
      "/api/v1/auth/guest-token",
    );
    return data;
  },
);

export const mintGuestTokenWithProfileFn = createServerFn({ method: "POST" })
  .inputValidator((d: AttachCustomerPayload) => d)
  .handler(async ({ data }): Promise<{ token: string; expiresAt: string }> => {
    const { data: res } = await vendure.post<{
      token: string;
      expiresAt: string;
    }>("/api/v1/auth/guest-token", data);
    return res;
  });

export async function fetchUserInfoFn(): Promise<AuthCustomer> {
  const { data } = await apiClient.get<AuthCustomer>("/api/v1/userinfo");
  return data;
}

export const userInfoQueryOptions = (enabled: boolean) =>
  queryOptions({
    queryKey: ["userinfo"],
    queryFn: fetchUserInfoFn,
    enabled,
    ...vendureCacheConfig,
  });

export async function updateUserInfoFn(
  firstName: string,
  lastName: string,
  emailAddress?: string,
): Promise<AuthCustomer> {
  const { data } = await apiClient.patch<AuthCustomer>("/api/v1/userinfo", {
    firstName,
    lastName,
    ...(emailAddress ? { emailAddress } : {}),
  });
  return data;
}

export async function createAddressFn(
  input: CreateAddressInput,
): Promise<CustomerAddress> {
  const { data } = await apiClient.post<CustomerAddress>(
    "/api/v1/userinfo/addresses",
    { ...input, defaultShippingAddress: false },
  );
  return data;
}

export async function deleteAddressFn(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/userinfo/addresses/${id}`);
}

export async function setDefaultAddressFn(id: string): Promise<void> {
  await apiClient.patch(`/api/v1/userinfo/addresses/${id}`, {
    defaultShippingAddress: true,
  });
}

export async function fetchAddressesFn(): Promise<CustomerAddress[]> {
  const { data } = await apiClient.get<CustomerAddress[]>(
    "/api/v1/userinfo/addresses",
  );
  return data;
}

export const addressesQueryOptions = (enabled: boolean) =>
  queryOptions({
    queryKey: ["userinfo", "addresses"],
    queryFn: fetchAddressesFn,
    enabled,
    ...vendureCacheConfig,
  });
